import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Params, QueryParamsHandling, Router, UrlSegment } from '@angular/router';
import { IInitialConfig } from '@app/core/venue-config/initial-config.model';
import { CRoutesWithParametersList, CRouteList } from '@app/route-list.const';
import { IDictionary } from '@core/common.model';
import { IProduct } from '@core/product/models/product.model';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs';
import { AnalyticsService } from '../analytics/analytics.service';
import { AppConfigService } from '../app-config/app-config.service';
import { InitialConfigService } from '../app-config/initial-config.service';
import { ProductService } from '../product/product.service';
import { VenueConfigService } from '../venue-config/venue-config.service';
import { ILoginRouteContext, INavigationRecord } from './navigation.model';
import { Location } from '@angular/common';
import { IProductQueryParams } from '@app/products/product-routing.model';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  defaultUrl: string;
  private baseVenueCheckoutUrlPortion = '/venueName/checkoutSlug/languageSlug';
  private originUrl = '';
  pageUrlSubject$ = new BehaviorSubject<string>('');

  private isLoading$ = new BehaviorSubject<boolean>(false);
  private navigationRecord: INavigationRecord = { currentUrl: undefined, previousUrl: undefined };

  readonly cRoutes = CRouteList;
  private loginRouteContext: ILoginRouteContext = { redirectUrl: this.cRoutes.account, queryParams: [] };

  constructor(
    private router: Router,
    private location: Location,
    private initialConfigService: InitialConfigService,
    private venueConfigService: VenueConfigService,
    private appConfigService: AppConfigService,
    private productService: ProductService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService
  ) {
    this.defaultUrl = this.appConfigService.getAppValue<string>('defaultRoute') as string;
  }

  getIsLoading(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  getTableNumberFromUrl(): string | null {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('table');
  }

  getExternalIdFromUrl(): string | null {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('externalId');
  }

  /**
   * Listens for router / navigation Start and End events and stores visited pages in urlHistory[].
   * @returns the current route url as string on navigation end
   */
  listenToRouteStartEnd(origin: string): Observable<string> {
    this.originUrl = origin;
    return this.router.events.pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter((event: any) => event instanceof NavigationEnd || event instanceof NavigationStart),
      map((route: NavigationEnd | NavigationStart) => {
        if (route instanceof NavigationStart || !this.navigationRecord.currentUrl) {
          this.setPreviousUrl(this.getCurrentURL());
          this.setCurrentUrl(route.url);
          this.isLoading$.next(true);
        }

        if (route instanceof NavigationEnd) {
          this.pageUrlSubject$.next(route.url);
          this.isLoading$.next(false);
        }

        return route.url;
      })
    );
  }

  /** Sets the original navigation route before redirection to log in flow occurred */
  setLoginRouteContext(redirectUrl: string, queryParams: Params = {}): void {
    this.loginRouteContext = {
      redirectUrl,
      queryParams
    };
  }

  /** Gets the original requested route before redirection to log in flow occurred */
  getLoginRouteContext(): ILoginRouteContext {
    return this.loginRouteContext;
  }

  /** Gets the route to redirect to after successful login/signup. Attempts to match child routes first */
  getLoginRouteRedirect(defaultRoute: string): string {
    const redirectUrl = this.loginRouteContext.redirectUrl.trim();
    let childRoute = Object.values(CRoutesWithParametersList).find((value: string) => {
      return redirectUrl.includes(value);
    });
    if (childRoute) {
      childRoute += redirectUrl.split('/').at(-1);
      return childRoute;
    }

    const route = Object.values(CRouteList).find((value: string) => {
      return !!this.loginRouteContext.redirectUrl.match(new RegExp(`${value}$`));
    });
    return route || defaultRoute;
  }

  /**
   * Navigates back a page if the user has visited a page previously
   * If no history (initial page load) it will navigate to the products page unless fallback specified
   * We use location.back() so we get scroll restoration i.e. from down in a products list -> product -> back to same place in list
   */
  async navigateBack(fallbackRoute = this.cRoutes.products): Promise<void> {
    if (this.getPreviousURL()) {
      this.location.back();
    } else {
      await this.navigateToUrl(fallbackRoute);
    }
  }

  /** Ensure we always go back out of products to different previous page and not a previous product page */
  navigateBackFromProductPage(): Promise<void | boolean> {
    const productPageRoute = this.getProductListPageRoute();
    const previousUrlRoute = this.getUrlWithoutBase(this.getPreviousURL() || this.router.url);
    if (previousUrlRoute.includes(this.cRoutes.productPage)) {
      return this.navigateToUrl(productPageRoute);
    }

    return this.navigateBack(productPageRoute);
  }

  /** Gets the full url of the /waiver page. */
  getWaiverRouteUrl(): string {
    return `${this.originUrl}${this.baseVenueCheckoutUrlPortion}${this.cRoutes.waiver}`;
  }

  /** Gets the base url of the /rsvp page. */
  getRsvpRouteUrl(): string {
    return `${this.originUrl}${this.baseVenueCheckoutUrlPortion}${this.cRoutes.rsvp}`;
  }

  /**
   * Translates the url into current language and navigates there.
   * @param url string in english to navigate to.
   */
  navigateToUrl(url: string, queryParams?: IDictionary<string | null>, replaceUrl = false, mergeParams = false) {
    const config = this.initialConfigService.getInitialConfig();
    if (url.charAt(0) !== '/') {
      url = `/${url}`;
    }

    let translatedPath = `/${config.siteKey}/${config.checkoutSlug}/${config.lang}${url}`;

    if (!config.routeHasVenueSlug) {
      translatedPath = `${config.checkoutSlug}/${config.lang}${url}`;
    }

    let queryParamsHandling: QueryParamsHandling = '';
    if (mergeParams) {
      queryParamsHandling = 'merge';
    }

    return this.router.navigate([translatedPath], { queryParams, replaceUrl, queryParamsHandling });
  }

  /**
   * Any page that does not require translated page with slug or lang etc
   * Think maintenance, error or 404 page.
   */
  navigateToCompleteUrl(url: string): void {
    this.router.navigate([url]);
  }

  /**
   * Checks the current url against an array of routes.
   * @param routes list of potential route endpoints from the CRouteList [routes.details, routes.products]
   * @param url current url advertised from this navigation service as a string `http://ecom.roller.com/${service.routes.details}`
   * @returns true if the url contains one of the routes.
   */
  checkRoutes(routes: string[], url: string): boolean {
    return routes.some((route: string) => url.includes(route));
  }

  /**
   * Gets current page url.
   * @returns Observable of the page Url.
   */
  getPageUrl(): Observable<string> {
    return this.pageUrlSubject$.asObservable();
  }

  /**
   * Get's the last recorded navigation end URL stored in memory
   * @param route string of the current route URL after navigating
   */
  setCurrentUrl(route: string | undefined): void {
    this.navigationRecord.currentUrl = route;
  }

  /**
   * Get's the last recorded navigation end URL stored in memory
   * @returns string of the last recorded page url
   */
  getCurrentURL(isFull = false): string | undefined {
    const currentUrl = this.navigationRecord.currentUrl;
    if (!isFull || currentUrl?.includes(this.originUrl)) {
      return currentUrl;
    }

    return this.originUrl + currentUrl;
  }

  /**
   * Get's the previous recorded URL stored in memory
   * @param route string of the previous page URL before navigating
   */
  setPreviousUrl(route: string | undefined): void {
    this.navigationRecord.previousUrl = route;
  }

  /**
   * Get's the previous recorded URL stored in memory
   * @returns string of the last recorded page url
   */
  getPreviousURL(): string | undefined {
    return this.navigationRecord.previousUrl;
  }

  /** Cleans up any url abnormalities and removes venue queryParam */
  cleanUpUrl(segments: UrlSegment[], queryParams: Params, config: IInitialConfig): void {
    // eslint-disable-next-line no-prototype-builtins
    if (queryParams.hasOwnProperty('venue')) {
      delete queryParams.venue;
    }

    const childPath = segments
      .splice(config.routeHasVenueSlug ? 3 : 2)
      .map((segment: UrlSegment) => segment.path)
      .join('/');

    let newUrl = `/${config.siteKey}/${config.checkoutSlug}/${config.lang}/${childPath}`;

    if (!config.routeHasVenueSlug) {
      newUrl = `/${config.checkoutSlug}/${config.lang}/${childPath}`;
    }

    this.router.navigate([newUrl], { queryParams });
  }

  /**
   * Uses the url to determine the route friendly name (for use in translateService.instant).
   * Get's the matching route list key based on the url a list value
   */
  currentUrlName(url: string): string {
    const name =
      (Object.keys(this.cRoutes) as (keyof typeof this.cRoutes)[]).find((key: keyof typeof this.cRoutes) =>
        url.includes(this.cRoutes[key])
      ) || '';
    return name.toLocaleUpperCase();
  }

  /** Calls on the window to open another blank tab. */
  navigateToExternalPage(url: string | undefined): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  /** Gets the venueConfig to decide to navigate to home (or products page depending on Config). */
  navigateHome(): Promise<boolean> {
    const homeUrl = this.venueConfigService.isHomePageEnabled() ? this.cRoutes.home : this.cRoutes.products;
    return this.navigateToUrl(homeUrl);
  }

  /**
   * Navigates to a specific product via query params and fires required analytics.
   * @param parentProductId the parent product to navigate to.
   * @param params query params to navigate with.
   * @param category translation key where the navigation is coming from, for analytics.
   * @param location where the action came from
   * @param replaceUrl boolean to replace the current url or not.
   * @param currentUrl the current url to navigate from.
   */
  async navigateToProduct(
    parentProductId: string,
    params: IProductQueryParams,
    category: string,
    location?: string,
    replaceUrl = false,
    currentUrl = this.getCurrentURL() || this.router.url
  ): Promise<void | boolean> {
    const products = this.productService.getProductListSync();
    const product = products.find((product: IProduct) => product.id === parentProductId);
    if (!product) {
      return this.navigateToUrl(this.getProductListPageRoute());
    }

    const route = `${this.cRoutes.productPage}${product.id}`;

    this.analyticsService.onProductClick(
      product,
      this.translateService.instant(category),
      this.translateService.instant(location || this.currentUrlName(currentUrl)),
      products.findIndex((prod: IProduct) => prod.id === product.id)
    );

    await this.navigateToUrl(route, params as unknown as IDictionary<string | null>, replaceUrl, true);
  }

  getProductListPageRoute(currentUrl = this.getRouteUrlSegmentsOnly() || this.router.url) {
    return currentUrl.includes(this.cRoutes.productMemberships)
      ? this.cRoutes.productMemberships
      : this.cRoutes.products;
  }

  /** Uses the venue config to set the common navigation url. */
  setBaseVenueCheckoutUrlPortion(config: IInitialConfig): void {
    this.baseVenueCheckoutUrlPortion = config.routeHasVenueSlug
      ? `/${config.siteKey}/${config.checkoutSlug}/${config.lang}`
      : `/${config.checkoutSlug}/${config.lang}`;
  }

  /**
   * Removes the /siteKey/checkoutSlug/language from the current Url.
   * * NOTE - pre-requisite is the config has been loaded and the baseVenueCheckoutUrlPortion set
   */
  getRouteUrlSegmentsOnly(): string {
    const currentUrl = this.getCurrentURL();
    if (!currentUrl) {
      return '';
    }

    return this.getUrlWithoutBase(currentUrl);
  }

  getUrlWithoutBase(url: string): string {
    return url.replace(this.originUrl, '').replace(this.baseVenueCheckoutUrlPortion, '');
  }

  /**
   * Ensures that if the checkoutSlug contains same name as a route that it is not included in check.
   * @param routeList list of routes to compare against example: [CRouteList.account]
   * @returns true is the url is found in the routeList
   */
  currentRouteContains(routeList: string[]): boolean {
    const segmentOnly = this.getRouteUrlSegmentsOnly();
    if (!segmentOnly) {
      return false;
    }

    return routeList.some((route: string) => segmentOnly.includes(route));
  }

  updateQueryParam(key: string, value: string | null) {
    return this.router.navigate([], {
      queryParams: { [key]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
